import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// 3rd party library
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

// custom function import
import { searchOption, timePeriodFilter } from "pages/notice/static/constant";
import { PLEASE_DELETE_THIS_BEFORE_DEPLOY } from "pages/notice/static/debug";
import routes from "pages/notice/static/route";
import { isNullOrUndefined } from "pages/notice/utility/utility";
import {
  fetchNotices,
  reloadNotices,
  setNoticeToHide,
  setNoticeToPin,
} from "shared/api/NoticeApi";
import { actionInAsyncGap } from "shared/lib/async/actionInAsyncGap";
import { applyDateFilter, isToday as isTodayDate } from "shared/lib/date/dateFilters";
import { formatToKST, toKstDate } from "shared/lib/date/formatKst";
import { ActionButton, EditButton } from "shared/ui/buttons";
import CircularProgressIndicator from "shared/ui/CircularProgressIndicator";
// custom components library
import { alertDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { AddHeader, AlertDiv, BaseContainer } from "shared/ui/layout";
import { Thumbnail } from "shared/ui/media";
import {
  DataTable,
  Pagination,
  SelectAllToggle,
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

import "bootstrap/dist/css/bootstrap.min.css";

// 백엔드 공지 페이로드의 게시일 필드는 createAt(철자상 'd' 없음)이다.
// 일부 응답은 createdAt로 올 수 있어 둘 다 안전하게 받는다.
const getNoticeCreatedAt = (notice) => notice?.createAt ?? notice?.createdAt;

// 검색 조건 드롭다운/빠른 기간 칩 옵션 (constant.js의 정의를 SearchPanel 형식으로 변환)
const SEARCH_CONDITION_OPTIONS = searchOption.map((option) => option.text);
const QUICK_RANGE_OPTIONS = timePeriodFilter.map((option) => ({
  label: option.text,
  value: option.state,
}));

const Notice = () => {
  const navigate = useNavigate();

  /// 상태 변수들 정의
  const [notices, setNotices] = useState([]);
  const [displayNotices, setDisplayNotices] = useState([]);
  const itemsPerPage = 10;

  // 선택된 공지글 (선택은 useTableSelection이 소유)
  const selection = useTableSelection(displayNotices);
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(displayNotices, { pageSize: itemsPerPage });

  // 검색 버튼으로 적용된 필터(정규화된 SearchPanel 출력). null이면 필터 없음(전체).
  const [appliedFilters, setAppliedFilters] = useState(null);

  // can be set to true between the async gap.
  const [loadingState, setLoadingState] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);

  const initialRender = useRef(true); // Create a ref for initial render

  const todayCreatedNoticeCount = notices.filter((notice) => {
    // createdAt은 서버가 UTC로 생성하므로 KST 보정 후 오늘 여부 비교.
    const kstDate = toKstDate(getNoticeCreatedAt(notice));
    return kstDate ? isTodayDate(kstDate) : false;
  }).length;

  const fetchErrorCb = (error) => {
    alertDialog(
      "공지사항을 불러오는 중 에러가 발생했습니다. 에러 사유: " + error,
      { title: "공지사항 불러오기 실패" },
    );
  };

  const computeFilteredNotices = useCallback((filters) => {
    const condition = filters?.condition;
    const keyword = filters?.keyword ?? "";
    const filteredNotices = [];
    let notice;
    for (var i = 0; i < notices.length; i++) {
      notice = notices[i];
      /*
                notice = {
                    "id" : Long,
                    "pin" : Boolean,
                    "hide" : Boolean,
                    "user" : User,
                    "files" : List<AttachFile>,
                    "title" : String,
                    "image" : String,
                    "content" : String,
                    "viewCnt" : Int,
                    "createAt" : LocalDateTime,
                    "updatedAt" : LocalDateTime,
                }
            */
      if (!isNullOrUndefined(notice["hide"]) && notice["hide"]) {
        // means notice is hide
        continue;
      }
      if (keyword) {
        let matchSearchQuery = false;
        if (condition === "제목") {
          matchSearchQuery = Boolean(notice.title?.includes(keyword));
        } else if (condition === "내용") {
          matchSearchQuery = Boolean(notice.content?.includes(keyword));
        } else if (condition === "제목 및 내용") {
          matchSearchQuery =
            Boolean(notice.title?.includes(keyword)) ||
            Boolean(notice.content?.includes(keyword));
        }
        if (!matchSearchQuery) continue;
      }

      // if it reaches here, that means notice matches the all filters
      filteredNotices.push(notice);
    }

    // 기간(게시일)은 SearchPanel이 빠른 칩/직접 범위를 정규화해 넘겨준다.
    // createAt은 서버 UTC라 KST 보정해 비교. 파싱 불가 시 Invalid Date로 제외.
    return applyDateFilter({
      data: filteredNotices,
      dateButtonClicked: filters?.dateButtonClicked ?? [
        false,
        false,
        false,
        false,
        false,
        false,
      ],
      startDate: filters?.startDate ?? "",
      endDate: filters?.endDate ?? "",
      getDate: (notice) => toKstDate(getNoticeCreatedAt(notice)) ?? new Date(NaN),
    });
  }, [notices]);

  // 검색 버튼에서만 호출: SearchPanel이 넘긴 필터를 적용하고, 결과가 없으면 안내한다.
  const handleSearchFilters = useCallback(
    (filters) => {
      setAppliedFilters(filters);
      const filteredNotices = computeFilteredNotices(filters);
      setDisplayNotices(filteredNotices);
      if (filteredNotices.length === 0) {
        alertDialog(
          "해당 검색어를 찾을 수 없습니다. \n검색어를 다시 확인하시고 검색해주세요.",
          { title: "검색 결과 없음." },
        );
      }
    },
    [computeFilteredNotices],
  );

  // fetch norices. called once when the component is loaded.
  useEffect(() => {
    PLEASE_DELETE_THIS_BEFORE_DEPLOY();
    actionInAsyncGap(
      setLoadingState,
      () => fetchNotices(setNotices),
      null,
      fetchErrorCb,
    );
  }, []);

  // 검색은 검색 버튼에서만 수행한다. 다만 데이터(notices)가 바뀌면
  // (최초 로드·삭제·고정 변경 후 재조회) 현재 필터를 조용히 재적용한다.
  // 검색어 입력만으로는 목록이 갱신되지 않는다.
  const computeRef = useRef(computeFilteredNotices);
  computeRef.current = computeFilteredNotices;
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;
  useEffect(() => {
    setDisplayNotices(computeRef.current(appliedFiltersRef.current));
  }, [notices]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (shouldReload) {
      setShouldReload(false);
      actionInAsyncGap(
        setLoadingState,
        () => reloadNotices(setNotices),
        null,
        fetchErrorCb,
      );
    }
  }, [shouldReload]);

  // 필터 초기화: 적용 필터 제거 후 숨김 제외 전체를 즉시 표시.
  const handleFilterReset = () => {
    setAppliedFilters(null);
    setDisplayNotices(
      notices.filter(
        (notice) => isNullOrUndefined(notice?.hide) || !notice.hide,
      ),
    );
  };
  const selectedNoticeIds = selection.selectedRows.map((notice) => notice.id);
  const handlePinNotices = () => {
    function successCb() {
      setShouldReload(true);
      selection.clear();
    }
    function errorCb(error) {
      alertDialog(
        "해당 공지를 고정하는 중 에러가 발생했습니다. \n같은 현상이 반복되면 관리자에게 문의해주시기 바랍니다. \n실패 사유: " +
          error,
        { title: "공지 글 고정 실패" },
      );
    }
    actionInAsyncGap(
      setLoadingState,
      () => setNoticeToPin(selectedNoticeIds, true),
      successCb,
      errorCb,
    );
  };
  const handleUnpinNotices = () => {
    function successCb() {
      setShouldReload(true);
      selection.clear();
    }
    function errorCb(error) {
      alertDialog(
        "해당 공지를 고정 해제하는 중 에러가 발생했습니다. \n같은 현상이 반복되면 관리자에게 문의해주시기 바랍니다. \n실패 사유: " +
          error,
        { title: "공지 글 고정 해제 실패" },
      );
    }
    actionInAsyncGap(
      setLoadingState,
      () => setNoticeToPin(selectedNoticeIds, false),
      successCb,
      errorCb,
    );
  };
  const handleDeleteNotices = async () => {
    function successCb() {
      setShouldReload(true);
      selection.clear();
    }
    function errorCb(error) {
      alertDialog(
        "해당 공지를 삭제하는 중 에러가 발생했습니다. \n같은 현상이 반복되면 관리자에게 문의해주시기 바랍니다. \n실패 사유: " +
          error,
        { title: "공지 글 삭제 실패" },
      );
    }
    actionInAsyncGap(
      setLoadingState,
      () => setNoticeToHide(selectedNoticeIds, true),
      successCb,
      errorCb,
    );
  };
  const handleCreateNotice = () => {
    navigate(routes.notice_create_route);
  };
  const handleNavigateToUpdate = (noticeId) => {
    navigate(`/notice/update/${noticeId}`);
  };

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      {
        Header: "제목",
        accessor: "title",
        Cell: ({ row }) => (
          <TitleCell>
            <Thumbnail src={row.original.image} alt={row.original.title} />
            <TitleText>{row.original.title}</TitleText>
          </TitleCell>
        ),
      },
      {
        Header: "작성자",
        id: "author",
        accessor: (row) => row.user?.name ?? "no name",
      },
      {
        Header: "게시일",
        id: "createdAt",
        accessor: getNoticeCreatedAt,
        // 서버가 UTC로 생성한 시각을 KST로 보정해 표시.
        Cell: ({ value }) => formatToKST(value),
      },
      {
        Header: "고정",
        id: "pin",
        accessor: (row) => Boolean(row.pin),
        Cell: ({ row }) => (
          <PinBadge $pinned={Boolean(row.original.pin)}>
            {row.original.pin ? "고정" : "-"}
          </PinBadge>
        ),
      },
      { Header: "조회수", accessor: "viewCnt" },
      {
        Header: "설정",
        id: "edit",
        Cell: ({ row }) => (
          <EditBtn onClick={() => handleNavigateToUpdate(row.original.id)}>
            설정
          </EditBtn>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <BaseContainer>
      <CircularProgressIndicator loading={loadingState} />

      {/* 페이지 맨 위 헤더 */}
      <AddHeader
        pageName="공지사항"
        onClick={handleCreateNotice}
        buttonValue="공지글 작성하기"
      />

      {/* 검색 필터 */}
      <SearchPanel onSearch={handleSearchFilters} onReset={handleFilterReset}>
        <SearchPanel.Keyword
          label="검색어"
          dropdown={SEARCH_CONDITION_OPTIONS}
          placeholder="검색어를 입력하세요"
        />
        <SearchPanel.QuickRange
          label="게시 기간"
          options={QUICK_RANGE_OPTIONS}
        />
        <SearchPanel.DateRange />
      </SearchPanel>

      {/* 선택한 공지글에 대한 작업 버튼 (HGU샵 관리와 동일한 레이아웃) */}
      <AlertDiv>
        <p>
          [오늘 등록된 새 글 <P>{todayCreatedNoticeCount}건</P>] 검색 결과{" "}
          <P>{displayNotices.length}건</P> 검색 되었습니다.
        </p>
        <ActionGroup>
          <SelectAllToggle
            totalCount={totalCount}
            selectedCount={selection.count}
            allSelected={selection.isAllSelected}
            onToggle={() =>
              selection.isAllSelected ? selection.clear() : selection.selectAll()
            }
          />
          <ActionButton
            onClick={handlePinNotices}
            disabled={selection.count === 0}
          >
            글 고정 지정
          </ActionButton>
          <ActionButton
            onClick={handleUnpinNotices}
            disabled={selection.count === 0}
          >
            글 고정 해제
          </ActionButton>
          <ActionButton
            onClick={handleDeleteNotices}
            disabled={selection.count === 0}
          >
            삭제
          </ActionButton>
        </ActionGroup>
      </AlertDiv>

      {/* 테이블 */}
      <div className="mt-3">
        <DataTable
          columns={columns}
          data={pageItems}
          selectable
          selection={selection}
        />
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </BaseContainer>
  );
};

export default Notice;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const P = styled.span`
  color: #0079e0;
  font-weight: bold;
`;

const TitleCell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const TitleText = styled.div`
  max-width: 500px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
`;

const EditBtn = styled(EditButton)`
  width: fit-content;
  height: fit-content;
  padding: 5px 10px;
`;

const PinBadge = styled.span`
  display: inline-block;
  min-width: 40px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: ${({ $pinned }) => ($pinned ? 600 : 400)};
  color: ${({ $pinned }) => ($pinned ? "#1c6dd0" : "#999")};
  background-color: ${({ $pinned }) => ($pinned ? "#e7f1ff" : "transparent")};
`;
